/**
 * AdminPage — gated by Emergent Google OAuth.
 *
 * Empty state: Google sign-in button. Signed-in but not whitelisted: an
 * "awaiting access" notice. Whitelisted admin: full upload + management UI.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    UploadCloud,
    Trash2,
    FileText,
    Film,
    Loader2,
    LogOut,
    BarChart3,
    Paperclip,
} from "lucide-react";
import { toast } from "sonner";
import { getMe, logout, startGoogleLogin } from "../lib/auth";
import { adminApi } from "../lib/adminApi";

export default function AdminPage() {
    const [authStatus, setAuthStatus] = useState("checking"); // checking | guest | non-admin | admin
    const [user, setUser] = useState(null);
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    // Skip /auth/me when returning from OAuth — AuthCallback handles that path.
    useEffect(() => {
        if (window.location.hash?.includes("session_id=")) {
            setAuthStatus("checking");
            return;
        }
        (async () => {
            try {
                const me = await getMe();
                setUser(me);
                setAuthStatus(me.is_admin ? "admin" : "non-admin");
            } catch (_e) {
                setAuthStatus("guest");
            }
        })();
    }, []);

    useEffect(() => {
        if (authStatus !== "admin") return;
        refresh();
    }, [authStatus]);

    async function refresh() {
        setLoading(true);
        try {
            const list = await adminApi.listVideos();
            setVideos(list);
            setError("");
        } catch (e) {
            setError(e?.response?.data?.detail || "Could not load videos.");
        } finally {
            setLoading(false);
        }
    }

    function handleSignIn() {
        sessionStorage.setItem("auth_return_to", "/admin");
        startGoogleLogin("/admin");
    }

    async function handleSignOut() {
        await logout();
        setUser(null);
        setAuthStatus("guest");
    }

    if (authStatus === "checking") {
        return (
            <PageShell>
                <p className="font-mono-os text-xs tracking-[0.36em] uppercase text-[#D4AF37] flex items-center gap-2">
                    <Loader2 size={14} className="animate-spin" /> Verifying session
                </p>
            </PageShell>
        );
    }

    if (authStatus === "guest") {
        return (
            <PageShell>
                <p className="font-mono-os text-xs tracking-[0.36em] uppercase text-[#D4AF37]">
                    Codex Admin
                </p>
                <h2 className="mt-3 font-serif-display text-4xl text-[#F9E596]">
                    Sign in to manage the Library.
                </h2>
                <p className="mt-3 text-[#CBD5E1] text-base max-w-md">
                    The admin console is restricted to whitelisted Codex stewards.
                    Sign in with Google to continue.
                </p>
                <button
                    data-testid="admin-google-signin"
                    onClick={handleSignIn}
                    className="codex-button mt-6 !px-6 !py-3 !text-sm"
                >
                    Continue with Google
                </button>
            </PageShell>
        );
    }

    if (authStatus === "non-admin") {
        return (
            <PageShell>
                <p className="font-mono-os text-xs tracking-[0.36em] uppercase text-[#F43F5E]">
                    Access Denied
                </p>
                <h2 className="mt-3 font-serif-display text-3xl text-[#F9E596]">
                    {user?.email} is not on the admin allowlist.
                </h2>
                <p className="mt-3 text-[#CBD5E1] text-base max-w-md">
                    Ask the OS owner to add this email to the `ADMIN_EMAILS`
                    environment variable on the backend, then sign in again.
                </p>
                <div className="mt-6 flex gap-3">
                    <button
                        data-testid="admin-logout"
                        onClick={handleSignOut}
                        className="codex-button !px-5 !py-2.5 !text-sm"
                    >
                        <LogOut size={15} /> Sign out
                    </button>
                </div>
            </PageShell>
        );
    }

    return (
        <PageShell>
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <p className="font-mono-os text-xs tracking-[0.36em] uppercase text-[#D4AF37]">
                        Codex Admin
                    </p>
                    <h2 className="mt-2 font-serif-display text-4xl text-[#F9E596]">
                        Library & Studies
                    </h2>
                    <p className="mt-2 text-[#CBD5E1] text-base">
                        Signed in as {user?.email}
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Link
                        to="/admin/analytics"
                        data-testid="admin-link-analytics"
                        className="codex-button !px-5 !py-2.5 !text-sm"
                    >
                        <BarChart3 size={15} /> Analytics
                    </Link>
                    <Link
                        to="/library"
                        data-testid="admin-link-library"
                        className="codex-button !px-5 !py-2.5 !text-sm"
                    >
                        <FileText size={15} /> Public Library
                    </Link>
                    <button
                        data-testid="admin-logout"
                        onClick={handleSignOut}
                        className="codex-button !px-5 !py-2.5 !text-sm"
                    >
                        <LogOut size={15} /> Sign out
                    </button>
                </div>
            </div>

            <div className="mt-8 grid lg:grid-cols-2 gap-6">
                <UploadVideoCard onUploaded={refresh} />
                <UploadStudyCard videos={videos} onUploaded={refresh} />
            </div>

            <div className="mt-10">
                <h3 className="font-serif-display text-3xl text-[#F9E596] mb-4">
                    Videos & attached studies
                </h3>
                {error && (
                    <p className="text-[#F43F5E] text-base" data-testid="admin-error">
                        {error}
                    </p>
                )}
                {loading ? (
                    <p className="text-[#CBD5E1] text-base flex items-center gap-2">
                        <Loader2 size={16} className="animate-spin" /> Loading…
                    </p>
                ) : videos.length === 0 ? (
                    <p className="text-[#CBD5E1] text-base" data-testid="admin-empty">
                        No videos yet. Upload the first one above.
                    </p>
                ) : (
                    <div className="space-y-4">
                        {videos.map((v) => (
                            <VideoRow key={v.id} video={v} onChange={refresh} />
                        ))}
                    </div>
                )}
            </div>
        </PageShell>
    );
}

function PageShell({ children }) {
    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-14 py-10 relative z-10">
            <div className="codex-card px-6 sm:px-8 py-8" data-testid="admin-shell">
                {children}
            </div>
        </div>
    );
}

function UploadVideoCard({ onUploaded }) {
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState(0);
    const inputRef = useRef(null);

    async function handleSubmit(e) {
        e.preventDefault();
        if (!file || !title.trim()) {
            toast.error("Provide a title and pick a video file.");
            return;
        }
        setBusy(true);
        setProgress(0);
        try {
            await adminApi.uploadVideo(file, title.trim(), description.trim(), setProgress);
            toast.success("Video uploaded.");
            setTitle("");
            setDescription("");
            setFile(null);
            if (inputRef.current) inputRef.current.value = "";
            onUploaded();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed.");
        } finally {
            setBusy(false);
            setProgress(0);
        }
    }

    return (
        <form
            data-testid="upload-video-card"
            className="codex-card p-6 space-y-4"
            onSubmit={handleSubmit}
        >
            <h4 className="font-serif-display text-xl text-[#F9E596] flex items-center gap-2">
                <Film size={18} className="text-[#D4AF37]" /> Upload video
            </h4>
            <input
                data-testid="upload-video-title"
                className="codex-input text-base"
                placeholder="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
            />
            <textarea
                data-testid="upload-video-desc"
                className="codex-input text-base min-h-[84px]"
                placeholder="Description (optional)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
            />
            <input
                ref={inputRef}
                data-testid="upload-video-file"
                type="file"
                accept="video/*"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="text-[#CBD5E1] text-base"
            />
            {busy && progress > 0 && (
                <div className="h-1.5 rounded-full bg-[#050B14]/60 overflow-hidden">
                    <span
                        className="block h-full bg-gradient-to-r from-[#D4AF37] to-[#F9E596]"
                        style={{ width: `${Math.round(progress * 100)}%` }}
                    />
                </div>
            )}
            <button
                data-testid="upload-video-submit"
                disabled={busy}
                className="codex-button !px-5 !py-2.5 !text-sm"
            >
                {busy ? <Loader2 size={15} className="animate-spin" /> : <UploadCloud size={15} />}
                {busy ? "Uploading…" : "Upload video"}
            </button>
        </form>
    );
}

function UploadStudyCard({ videos, onUploaded }) {
    const [title, setTitle] = useState("");
    const [videoId, setVideoId] = useState("");
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const inputRef = useRef(null);

    async function handleSubmit(e) {
        e.preventDefault();
        if (!file || !title.trim()) {
            toast.error("Provide a title and pick a PDF.");
            return;
        }
        setBusy(true);
        try {
            await adminApi.uploadStudy(file, title.trim(), videoId || null);
            toast.success("Study uploaded.");
            setTitle("");
            setVideoId("");
            setFile(null);
            if (inputRef.current) inputRef.current.value = "";
            onUploaded();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed.");
        } finally {
            setBusy(false);
        }
    }

    return (
        <form
            data-testid="upload-study-card"
            className="codex-card p-6 space-y-4"
            onSubmit={handleSubmit}
        >
            <h4 className="font-serif-display text-xl text-[#F9E596] flex items-center gap-2">
                <FileText size={18} className="text-[#D4AF37]" /> Upload PDF study
            </h4>
            <input
                data-testid="upload-study-title"
                className="codex-input text-base"
                placeholder="Study title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
            />
            <select
                data-testid="upload-study-video"
                className="codex-input text-base"
                value={videoId}
                onChange={(e) => setVideoId(e.target.value)}
            >
                <option value="">Not attached to a video</option>
                {videos.map((v) => (
                    <option key={v.id} value={v.id}>
                        {v.title}
                    </option>
                ))}
            </select>
            <input
                ref={inputRef}
                data-testid="upload-study-file"
                type="file"
                accept="application/pdf,.pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="text-[#CBD5E1] text-base"
            />
            <button
                data-testid="upload-study-submit"
                disabled={busy}
                className="codex-button !px-5 !py-2.5 !text-sm"
            >
                {busy ? <Loader2 size={15} className="animate-spin" /> : <UploadCloud size={15} />}
                {busy ? "Uploading…" : "Upload study"}
            </button>
        </form>
    );
}

function VideoRow({ video, onChange }) {
    const [busy, setBusy] = useState(false);

    async function handleDeleteVideo() {
        if (!window.confirm(`Delete "${video.title}"? Attached studies will be detached, not deleted.`))
            return;
        setBusy(true);
        try {
            await adminApi.deleteVideo(video.id);
            toast.success("Video deleted.");
            onChange();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed.");
        } finally {
            setBusy(false);
        }
    }

    async function handleDeleteStudy(s) {
        if (!window.confirm(`Delete study "${s.title}"?`)) return;
        try {
            await adminApi.deleteStudy(s.id);
            toast.success("Study deleted.");
            onChange();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed.");
        }
    }

    async function handleDetach(s) {
        try {
            await adminApi.attachStudy(s.id, null);
            toast.success("Detached from video.");
            onChange();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Detach failed.");
        }
    }

    return (
        <div
            data-testid={`video-row-${video.id}`}
            className="codex-card p-5 sm:p-6 space-y-3"
        >
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <p className="font-serif-display text-xl text-[#F9E596] leading-tight">
                        {video.title}
                    </p>
                    {video.description && (
                        <p className="text-[#CBD5E1] text-base mt-1">{video.description}</p>
                    )}
                    <p className="font-mono-os text-xs tracking-[0.28em] uppercase text-[#94A3B8] mt-2">
                        {(video.size / 1_000_000).toFixed(1)} MB · {video.view_count} views
                    </p>
                </div>
                <button
                    data-testid={`delete-video-${video.id}`}
                    onClick={handleDeleteVideo}
                    disabled={busy}
                    className="codex-icon-button !border-[#F43F5E]/60 !text-[#F43F5E]"
                    aria-label="Delete video"
                >
                    <Trash2 size={17} />
                </button>
            </div>
            {video.studies?.length > 0 && (
                <div className="border-t border-[#D4AF37]/15 pt-3">
                    <p className="font-mono-os text-xs tracking-[0.28em] uppercase text-[#D4AF37] mb-2">
                        Attached studies
                    </p>
                    <ul className="space-y-2">
                        {video.studies.map((s) => (
                            <li
                                key={s.id}
                                data-testid={`study-row-${s.id}`}
                                className="flex items-center justify-between gap-3 text-base text-[#F1F5F9] bg-[#0A1220]/50 border border-[#D4AF37]/20 rounded-md px-3 py-2.5"
                            >
                                <span className="flex items-center gap-2 min-w-0">
                                    <Paperclip size={15} className="text-[#D4AF37]" />
                                    <span className="truncate">{s.title}</span>
                                    <span className="font-mono-os text-xs text-[#94A3B8]">
                                        {s.download_count} dl
                                    </span>
                                </span>
                                <span className="flex items-center gap-3 flex-shrink-0">
                                    <button
                                        onClick={() => handleDetach(s)}
                                        className="font-mono-os text-xs tracking-[0.2em] uppercase text-[#94A3B8] hover:text-[#F9E596]"
                                    >
                                        Detach
                                    </button>
                                    <button
                                        onClick={() => handleDeleteStudy(s)}
                                        className="text-[#F43F5E] hover:text-[#FF6E81]"
                                        aria-label="Delete study"
                                    >
                                        <Trash2 size={15} />
                                    </button>
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
